import { pushPreSentryMetric } from 'core/utils/perf.js'
import { getClient, isInitialized, setMeasurement, setTag } from 'src/common/sentry-client'

const HOME_STARTUP_WINDOW_MS = 10000
const FINALIZE_TIMEOUT_MS = 15000
const SLOW_API_THRESHOLD_MS = 1200

const TARGETS = {
  fcp: 1800,
  lcp: 2500,
  firstBanner: 2200,
  tbt: 300,
  longAnimationFrameCount: 5,
  startupGraphqlRequestCount: 8,
}

function sentryReady() {
  return isInitialized() || !!getClient()
}

function setMeasurementSafe(name, value, unit = 'millisecond') {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return
  }

  if (sentryReady()) {
    setMeasurement(name, value, unit)
    return
  }

  pushPreSentryMetric(name, value, unit)
}

function defaultState() {
  return {
    active: false,
    startedAt: null,
    fcp: null,
    lcp: null,
    firstBanner: null,
    startupGraphqlRequestCount: 0,
    externalApiSlowCount: 0,
    longAnimationFrameCount: 0,
    mainThreadTotalBlockingTime: 0,
    finalized: false,
    cleanup: null,
    finalizeTimer: null,
  }
}

function getGlobalWindow() {
  return typeof window === 'undefined' ? null : window
}

function getState() {
  const globalWindow = getGlobalWindow()
  if (!globalWindow) {
    return defaultState()
  }

  if (!globalWindow.__HOME_PERF) {
    globalWindow.__HOME_PERF = defaultState()
  }

  return globalWindow.__HOME_PERF
}

function markFcp(entry, state) {
  const globalWindow = getGlobalWindow()
  const startTime = globalWindow?.appStartTime || 0
  const elapsed = Math.max(0, Math.round(entry.startTime - startTime))
  state.fcp = state.fcp ?? elapsed
}

function markLcp(entry, state) {
  const globalWindow = getGlobalWindow()
  const startTime = globalWindow?.appStartTime || 0
  state.lcp = Math.max(0, Math.round(entry.startTime - startTime))
}

function addLongFrame(duration, state) {
  state.longAnimationFrameCount += 1
  state.mainThreadTotalBlockingTime += Math.max(0, Math.round(duration - 50))
}

function finalizeHomeObservability(reason = 'done') {
  const state = getState()

  if (!state.active || state.finalized) {
    return
  }

  state.finalized = true

  if (state.cleanup) {
    state.cleanup()
  }

  if (state.finalizeTimer) {
    clearTimeout(state.finalizeTimer)
  }

  setMeasurementSafe('app.home.fcp', state.fcp, 'millisecond')
  setMeasurementSafe('app.home.lcp', state.lcp, 'millisecond')
  setMeasurementSafe('app.home.first_banner_render', state.firstBanner, 'millisecond')
  setMeasurementSafe('app.home.long_animation_frame_count', state.longAnimationFrameCount, 'none')
  setMeasurementSafe('app.home.main_thread_total_blocking_time', state.mainThreadTotalBlockingTime, 'millisecond')
  setMeasurementSafe('app.home.startup_graphql_request_count', state.startupGraphqlRequestCount, 'none')
  setMeasurementSafe('app.home.external_api_slow_count', state.externalApiSlowCount, 'none')

  const regressionSignals = [
    typeof state.fcp === 'number' && state.fcp > TARGETS.fcp,
    typeof state.lcp === 'number' && state.lcp > TARGETS.lcp,
    typeof state.firstBanner === 'number' && state.firstBanner > TARGETS.firstBanner,
    state.mainThreadTotalBlockingTime > TARGETS.tbt,
    state.longAnimationFrameCount > TARGETS.longAnimationFrameCount,
    state.startupGraphqlRequestCount > TARGETS.startupGraphqlRequestCount,
  ]
  const regressionCount = regressionSignals.filter(Boolean).length

  setMeasurementSafe('app.home.regression_signal_count', regressionCount, 'none')

  if (sentryReady()) {
    setTag('home.perf.observed', true)
    setTag('home.perf.finalize_reason', reason)
    setTag('home.perf.external_api_slow', state.externalApiSlowCount > 0 ? 'yes' : 'no')
    setTag('home.perf.regression', regressionCount > 0 ? 'yes' : 'no')
  }

  state.active = false
}

export function startHomeObservability() {
  const globalWindow = getGlobalWindow()
  if (!globalWindow || typeof performance === 'undefined') {
    return () => {}
  }

  const state = getState()

  if (state.active) {
    return () => finalizeHomeObservability('restart')
  }

  Object.assign(state, defaultState(), {
    active: true,
    startedAt: performance.now(),
  })

  const observers = []
  const listeners = []

  const registerObserver = (observer) => {
    observers.push(observer)
    return observer
  }

  const disconnectAllObservers = () => {
    observers.forEach((observer) => observer.disconnect())
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      finalizeHomeObservability('hidden')
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange, { passive: true })
  listeners.push(() => document.removeEventListener('visibilitychange', onVisibilityChange))

  const onPageHide = () => finalizeHomeObservability('pagehide')
  globalWindow.addEventListener('pagehide', onPageHide, { passive: true })
  listeners.push(() => globalWindow.removeEventListener('pagehide', onPageHide))

  if (globalWindow.PerformanceObserver) {
    try {
      const paintObserver = registerObserver(
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              markFcp(entry, state)
            }
          }
        }),
      )
      paintObserver.observe({ type: 'paint', buffered: true })
    } catch (error) {
      void error
    }

    try {
      const lcpObserver = registerObserver(
        new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const last = entries[entries.length - 1]
          if (last) {
            markLcp(last, state)
          }
        }),
      )
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch (error) {
      void error
    }

    const supportsLongAnimationFrame =
      Array.isArray(globalWindow.PerformanceObserver.supportedEntryTypes) &&
      globalWindow.PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')

    if (supportsLongAnimationFrame) {
      try {
        const longAnimationFrameObserver = registerObserver(
          new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              addLongFrame(entry.duration, state)
            }
          }),
        )
        longAnimationFrameObserver.observe({ type: 'long-animation-frame', buffered: true })
      } catch (error) {
        void error
      }
    } else {
      try {
        const longTaskObserver = registerObserver(
          new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              addLongFrame(entry.duration, state)
            }
          }),
        )
        longTaskObserver.observe({ type: 'longtask', buffered: true })
      } catch (error) {
        void error
      }
    }
  }

  state.cleanup = () => {
    disconnectAllObservers()
    listeners.forEach((removeListener) => removeListener())
  }

  state.finalizeTimer = globalWindow.setTimeout(() => {
    finalizeHomeObservability('timeout')
  }, FINALIZE_TIMEOUT_MS)

  return () => finalizeHomeObservability('unmount')
}

export function markHomeFirstBannerRendered() {
  const state = getState()
  const globalWindow = getGlobalWindow()

  if (!state.active || typeof state.firstBanner === 'number' || typeof performance === 'undefined') {
    return
  }

  const startTime = globalWindow?.appStartTime || 0
  state.firstBanner = Math.max(0, Math.round(performance.now() - startTime))
}

export function trackHomeStartupGraphqlRequest(durationMs) {
  const state = getState()
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now()

  if (!state.active || state.finalized || typeof state.startedAt !== 'number') {
    return
  }

  if (now - state.startedAt > HOME_STARTUP_WINDOW_MS) {
    return
  }

  state.startupGraphqlRequestCount += 1

  if (durationMs >= SLOW_API_THRESHOLD_MS) {
    state.externalApiSlowCount += 1
  }
}
